export const unitGuides = [
  {
    courseKey: 'physics',
    unitNumber: 1,
    title: 'Unit 1: Laws Governing Motion',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 11 Physics',
    dateRange: 'August 4, 2025 - October 7, 2025',
    totalDays: '46 days',
    sourceLabel: '2025-2026_Unit_Guide_1_Physics.pdf',
    sourceUrl: '/original_pdfs/physics/2025-2026_Unit_Guide_1_Physics.pdf',
    transcriptPath: '/guides/physics-unit1-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students focus on foundational ideas of physics, motion, and force, including scientific process skills, graphing relationships, kinematics, and Newtonian force models.',
          'The guide organizes Unit 1 into process-of-science foundations, motion analysis, and forces/equilibrium applications.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Chapter Pacing',
        bullets: [
          'Chapters 0-1 The Process of Science and Introduction to Physics: 5 A/B days',
          'Chapters 2-3 Motion: 7 A/B days',
          'Chapters 4-5 Forces and Motion: 8 A/B days'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: 5.A, 5.C, 5.E, 5.F, 5.G',
          'Supporting emphasis: 5.B, 5.D',
          'SEPs and RTCs are embedded across Unit 1 lesson blocks.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'When one property in a motion system changes, how are related properties affected?',
          'What core understandings of motion laws can be expressed from scenarios, problems, and experimentation?',
          'What is the value of observing patterns and relationships in data?'
        ]
      },
      {
        id: 'projects',
        heading: 'Suggested Projects',
        bullets: [
          'Ch. 0 STEM Project: Engineering Design Process',
          'Ch. 1 STEM Project: Compare Flight of a Paper Airplane',
          'Ch. 4 STEM Project: Design and Build a Rocket'
        ]
      },
      {
        id: 'chapter-0-1',
        heading: 'Chapters 0-1 Process of Science and Intro to Physics',
        bullets: [
          'Chapter 0 includes What Is Science, Value of Science, Equity in Science, and Science and Engineering Practices.',
          'Chapter 1 includes What Is Physics, Measurement and SI Units, Uncertainty in Data, and Representing Data.',
          'Emphasis areas: safety, scientific communication, data quality, SI units, dimensional analysis, graph interpretation.'
        ]
      },
      {
        id: 'chapter-2-3',
        heading: 'Chapters 2-3 Motion',
        bullets: [
          'Chapter 2 covers motion diagrams, coordinate systems, position-time graphs, speed, and velocity.',
          'Chapter 3 covers acceleration, constant-acceleration motion, and free-fall models.',
          'Emphasis areas: scalar/vector reasoning, frame of reference, graph/equation translation, kinematics interpretation.'
        ]
      },
      {
        id: 'chapter-4-5',
        heading: 'Chapters 4-5 Forces and Motion',
        bullets: [
          'Chapter 4 covers force types, free-body diagrams, Newton\'s first/second/third laws, weight, drag, and interaction pairs.',
          'Chapter 5 covers vectors in two dimensions, friction models, and equilibrium/inclined planes.',
          'Emphasis areas: balancing/unbalancing systems, force components, resultant/equilibrant analysis, friction coefficients.'
        ]
      },
      {
        id: 'vocab',
        heading: 'Vocabulary and Clarifications',
        bullets: [
          'Guide includes chapter-by-chapter prior terms, lesson vocabulary, and supporting vocabulary.',
          'Instructional sections include misconceptions, process notes, scaffolds, and extension activities.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the full transcript viewer below for complete extracted text from the uploaded PDF, including page-by-page details.'
        ]
      }
    ]
  },
  {
    courseKey: 'physics',
    unitNumber: 2,
    title: 'Unit 2: Energy and Momentum',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 11 Physics',
    dateRange: 'October 20, 2025 - December 19, 2025',
    totalDays: '20 A / 19 B instructional days',
    sourceLabel: '2024-2025_Physics_Unit_Guide_2.pdf',
    sourceUrl: '/original_pdfs/physics/2024-2025_Physics_Unit_Guide_2.pdf',
    transcriptPath: '/guides/physics-unit2-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Unit 2 emphasizes projectile and gravitational motion, then transitions into momentum/impulse and work-energy relationships.',
          'Students model two-dimensional motion, apply force and gravitation concepts, analyze collision systems, and use conservation frameworks for momentum and energy.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Chapter Pacing',
        bullets: [
          'Chapters 6-8 Projectile and Gravitational Motion: 6 A/B days',
          'Chapter 9 Momentum: 6 A/B days',
          'Chapters 10-12 Energy: 6 A/B days'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Projectile/gravitation focus: 5.C, 5.D, 5.E, 5.F, 5.H',
          'Momentum focus: 7.D, 7.E',
          'Energy focus: 7.A, 7.B, 7.C (with supporting links to force and momentum contexts)'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How can laws governing two-dimensional motion be calculated and expressed?',
          'How are circular and gravitational relationships modeled and interpreted?',
          'How do mechanical-energy calculations reinforce conservation of energy?',
          'How are impulse and momentum used to explain collisions, safety, and propulsion?'
        ]
      },
      {
        id: 'projects',
        heading: 'Suggested Projects',
        bullets: [
          'pg. 247 Design a highway interchange',
          'pg. 287 Model a planetary system',
          'pg. 341 Model motion on a roller coaster',
          'pg. 399 Predict effect of a car crash',
          'pg. 441 Evaluate household energy conservation and efficiency',
          'pg. 485 Design temperature protection for medicine',
          'pg. 529 Explain energy transformation'
        ]
      },
      {
        id: 'chapter-6-8',
        heading: 'Chapters 6-8 Projectile and Gravitational Motion',
        bullets: [
          'Launch contexts include projectile-path modeling and gravitational/orbital investigations.',
          'Key lesson threads: projectile components, circular motion, relative motion, universal gravitation, and Sun-Earth-Moon systems.',
          'Guide notes indicate rotational dynamics coverage in Chapter 8 as optional beyond tested TEKS.'
        ]
      },
      {
        id: 'chapter-9',
        heading: 'Chapter 9 Momentum',
        bullets: [
          'Core focus: impulse-momentum theorem and conservation of momentum in one-dimensional collisions.',
          'Applications include automobile safety, athletics, recoil/propulsion, and model-based collision analysis.',
          'Guide highlights qualitative analysis requirements for elastic vs inelastic collisions.'
        ]
      },
      {
        id: 'chapter-10-12',
        heading: 'Chapters 10-12 Energy',
        bullets: [
          'Chapter 10 addresses work, power, mechanical/kinetic/potential energy, and conservation of energy.',
          'Guide marks portions of Chapters 11 and 12 as optional/extended beyond tested TEKS.',
          'Instruction emphasizes energy transformations, diagrams, and relationship to system efficiency.'
        ]
      },
      {
        id: 'vocab',
        heading: 'Vocabulary and Clarifications',
        bullets: [
          'Guide includes chapter-by-chapter prior terms, lesson vocabulary, and supporting vocabulary.',
          'Instructional sections include misconceptions, process notes, scaffolds, and extensions.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the full transcript viewer below for complete extracted text from the uploaded PDF, including page-by-page details.'
        ]
      }
    ]
  },
  {
    courseKey: 'physics',
    unitNumber: 3,
    title: 'Unit 3: Waves',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 11 Physics',
    dateRange: 'January 7, 2026 - March 6, 2026',
    totalDays: '42 total days (21 A/B Days)',
    sourceLabel: '2025-2026_Physics_Unit_3_Guide_ (1).pdf',
    sourceUrl: '/original_pdfs/physics/2025-2026_Physics_Unit_3_Guide_1.pdf',
    transcriptPath: '/guides/physics-unit3-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students explore general wave characteristics, compare sound and light waves, and analyze how waves behave in different media.',
          'Students calculate wave speed using wavelength and frequency, apply reflection/refraction ideas, investigate image formation in mirrors and lenses, and use Malus\'s Law to determine polarized-light intensity.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Chapter Pacing',
        bullets: [
          'Chapters 15-16 Vibrations, Waves, and Sound: 6 A/B days',
          'Chapter 17 Fundamentals of Light: 6 A/B days',
          'Chapters 18-20 Reflection, Refraction, Interference, and Diffraction: 6 A/B days'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: 8.C, 8.D, 9.B',
          'Supporting emphasis: 8.A, 8.B, 8.G',
          'SEPs/RTCs: Patterns, Models, Systems, Structure and Function'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What are common types of repetitive motion and waves?',
          'What happens when waves interact?',
          'How do wave properties affect the sounds you hear?',
          'How is pitch controlled in musical instruments?',
          'What do echolocation and ultrasonic imaging have in common?',
          'What factors affect brightness and polarization of light?',
          'How do scientists use the Doppler effect to determine motion of stars and galaxies?',
          'How do mirrors and lenses form images?',
          'How are interference and diffraction of light related?'
        ]
      },
      {
        id: 'projects',
        heading: 'Suggested Projects',
        bullets: [
          'pg. 683 Develop an informational article on regional seismic activity',
          'pg. 735 Measure distance using sound',
          'pg. 779 Assess importance of absorbency related to lasers',
          'pg. 819 Explain fundamentals of space telescopes',
          'pg. 861 Model the path of a light ray',
          'pg. 903 Compare uses of holograms in engineering'
        ]
      },
      {
        id: 'chapter-15-16',
        heading: 'Chapters 15-16 Vibrations, Waves, and Sound',
        bullets: [
          'Core TEKS: 8.A, 8.B, 8.C, 8.D',
          'Chapter 15 Launch pg. 683 (Tsunamis; Launch Lab: Types of Waves)',
          'Chapter 16 Launch pg. 735 (Sound; Launch Lab: Producing Musical Notes)',
          'Instructional themes include periodic motion, wave properties/behavior, seismic waves, sound detection, physics of music, and uses of sound waves.'
        ]
      },
      {
        id: 'chapter-17',
        heading: 'Chapter 17 Fundamentals of Light',
        bullets: [
          'Core TEKS: 8.D and 9.B',
          'Chapter 17 Launch pg. 779 (Light; Launch Lab: Light\'s Path)',
          'Focus topics: illumination, wave nature of light, diffraction/color, polarization, Malus\'s Law, speed of light, and Doppler shift.'
        ]
      },
      {
        id: 'chapter-18-20',
        heading: 'Chapters 18-20 Reflection, Refraction, Interference, Diffraction',
        bullets: [
          'Core TEKS: 8.D and 8.G',
          'Chapter 18 Launch pg. 819 (Mirrors and Telescopes; Images from Mirrors)',
          'Chapter 19 Launch pg. 861 (Lenses; Broken Straw Appearance)',
          'Chapter 20 Launch pg. 903 (Iridescence in Nature; Patterns in Light)',
          'Focus topics: law of reflection, ray diagrams, Snell\'s law, lens equations, optical instruments, thin-film and double-slit interference, diffraction gratings.'
        ]
      },
      {
        id: 'vocab',
        heading: 'Vocabulary and Clarifications',
        bullets: [
          'Vocabulary spans periodic motion, wave metrics, sound systems, light models, geometric optics, interference, and diffraction.',
          'Grade-level clarifications connect prior learning in Grade 9/10 and extend toward Grade 12 applications.',
          'Instructional approaches include common misconceptions, process notes, scaffolds, and extensions.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the full transcript viewer below for complete extracted text from the uploaded PDF, including page-by-page details.'
        ]
      }
    ]
  },
  {
    courseKey: 'economics',
    unitNumber: 1,
    title: 'Unit 1: Fundamentals of Economics',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 Economics',
    dateRange: 'August 4, 2025 - August 26, 2025',
    totalDays: 'Fall: 9 A/8 B (includes class structures) | Spring: 8 A/B (includes class structures)',
    sourceLabel: '2025-2026_Economics_12th_Grade_Unit_1_Guide-1.pdf',
    sourceUrl:
      '/original_pdfs/economics/2025-2026_Economics_12th_Grade_Unit_1_Guide-1.pdf',
    transcriptPath: '/guides/economics-unit1-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students build foundations in scarcity, choice, factors of production, and economic systems.',
          'The unit emphasizes how limited resources shape individual and societal decisions about production, consumption, and distribution.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Pacing Highlights',
        bullets: [
          'Fall window: August 4-26, 2025 with an opening class-structures day.',
          'Spring repeat window: January 7-30, 2026 with an opening class-structures day.',
          'Lesson sequence includes scarcity, circular flow, opportunity cost, PPCs, systems comparison, and economic philosophers.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Core TEKS: E.1A, E.1B, E.1C, E.1D, E.4B, E.4C, E.5A.',
          'Priority language stresses scarcity, opportunity cost, factors of production, and system characteristics.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What are the basic economic questions faced by societies?',
          'What are the factors of production?',
          'What can be learned from examining a production possibilities curve?',
          'How do scarcity and opportunity costs affect economic choices?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Create a poster, presentation, or infographic summarizing fundamental economic concepts and their impact on society and individuals.',
          'Use TEKS-aligned evidence and clear economic vocabulary for audience-facing communication.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Clarify demand vs quantity demanded and supply vs quantity supplied.',
          'Address confusion between economic-system labels and real-world mixed systems.',
          'Reinforce that scarcity and trade-offs apply to both personal and public decision making.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 1 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'economics',
    unitNumber: 2,
    title: 'Unit 2: The Market System',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 Economics',
    dateRange: 'August 27, 2025 - September 22, 2025',
    totalDays: 'Fall: 9 A/B | Spring: 10 A/9 B',
    sourceLabel: '2025-2026_Economics_12th_Grade_Unit_2_Guide-1.pdf',
    sourceUrl:
      '/original_pdfs/economics/2025-2026_Economics_12th_Grade_Unit_2_Guide-1.pdf',
    transcriptPath: '/guides/economics-unit2-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students examine supply-demand dynamics, equilibrium, market structures, and trade relationships across local and global contexts.',
          'Instruction develops graphing fluency and comparative-advantage reasoning for market decision making.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Pacing Highlights',
        bullets: [
          'Fall window: August 27-September 22, 2025.',
          'Spring repeat window: February 2-26, 2026.',
          'Lesson sequence includes curve shifts, equilibrium graphing, comparative advantage, trade barriers, exchange rates, and free-enterprise benefits.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Core TEKS: E.2A, E.2B, E.2C, E.3A, E.3B, E.3C, E.4A, E.5A, E.10C.',
          'Priority language stresses market response to price and non-price determinants, plus trade-growth connections.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What is different about supply and demand versus quantity supplied and quantity demanded?',
          'How do price changes impact quantity supplied and quantity demanded?',
          'How is an equilibrium price determined?',
          'What non-price determinants affect supply and demand?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Create an infographic explaining factors that influence global trade markets.',
          'Final product should be accurate, evidence-based, and visually clear for a public audience.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address confusion between curve shifts and movement along curves.',
          'Correct the misconception that highest output always means comparative advantage.',
          'Reinforce equilibrium as interaction of both demand and supply conditions, not one-sided pricing.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 2 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'economics',
    unitNumber: 3,
    title: 'Unit 3: Macroeconomics and Entrepreneurship',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 Economics',
    dateRange: 'September 23, 2025 - October 22, 2025',
    totalDays: 'Fall: 7 A/B | Spring: 7 A/B',
    sourceLabel: '2025-2026_Economics_12th_Grade_Unit_3_Guide-1.pdf',
    sourceUrl:
      '/original_pdfs/economics/2025-2026_Economics_12th_Grade_Unit_3_Guide-1.pdf',
    transcriptPath: '/guides/economics-unit3-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students analyze macroeconomic indicators, business cycles, and entrepreneurship as drivers of innovation and growth.',
          'The unit links firms, households, institutions, and policy tools in a circular-flow and free-enterprise framework.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Pacing Highlights',
        bullets: [
          'Fall window: September 23-October 22, 2025.',
          'Spring repeat window: February 27-April 1, 2026.',
          'Lesson sequence includes business cycles, money supply shifts, financial institutions, tax policy, and entrepreneurship structures.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes E.6A, E.7A, E.8A, E.9A, E.9B, E.13A, E.15A, E.16A.',
          'Supporting emphasis includes E.6B, E.8B, E.10A, E.10B, E.10C, E.13B, E.15B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How do taxes and policy decisions support or constrain the broader economy?',
          'What is the role of the Federal Reserve in economic stability?',
          'How should business-cycle data be used in policy and business planning?',
          'What role should government play in a free-enterprise economy?',
          'Why are business organizations essential for economic development?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Complete the Unit 3 performance task product using the district rubric and criteria.',
          'Demonstrate macroeconomic reasoning with data, policy interpretation, and clear written/visual explanation.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address confusion around GDP, unemployment, inflation, and business-cycle interpretation.',
          'Clarify relationships among government regulation, entrepreneurship, and growth.',
          'Reinforce differences among sole proprietorships, partnerships, and corporations.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 3 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'economics',
    unitNumber: 4,
    title: 'Unit 4: Government and the Economy',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 Economics',
    dateRange: 'October 23, 2025 - November 13, 2025 (Planner Window)',
    totalDays: 'Guide Header: Fall 7 A/B | Spring 7 A/B',
    sourceLabel: '2025-2026_Grade_12th_Economics_Uni_4_Guide.pdf',
    sourceUrl:
      '/original_pdfs/economics/2025-2026_Grade_12th_Economics_Uni_4_Guide.pdf',
    transcriptPath: '/guides/economics-unit4-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students examine how government institutions and policy tools influence monetary conditions, fiscal outcomes, and economic performance.',
          'Instruction emphasizes Federal Reserve structure, taxation, budget decisions, and policy tradeoffs in a free-enterprise context.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Pacing Highlights',
        bullets: [
          'Fall window: October 23-November 13, 2025.',
          'Spring repeat window: April 2-April 27, 2026.',
          'Lesson sequence includes money forms, Federal Reserve structure, monetary policy tools, taxation, fiscal policy, and public-policy tradeoffs.'
        ]
      },
      {
        id: 'source-notes',
        heading: 'Guide Source Notes',
        bullets: [
          'The Unit 4 PDF header shows Fall October 24-November 14 and Spring April 1-April 7.',
          'To stay aligned with lessons and planner dates used in-app, this guide uses the district scope-sequence/planner windows: Fall October 23-November 13 and Spring April 2-April 27.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes E.12A, E.13A, and E.14A.',
          'Supporting emphasis includes E.5B, E.7B, E.11B, E.11C, E.12B, E.12C, E.12D, E.13B, E.14B, and E.14C.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How has the value of the U.S. dollar changed since leaving the gold standard?',
          'What are the advantages and disadvantages of the Federal Reserve System?',
          'What taxes are used as government revenue streams at local, state, and national levels?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Complete the Unit 4 performance task product using district criteria and rubric expectations.',
          'Support policy claims with monetary/fiscal evidence and clear explanation of economic impact.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address confusion between federal funds rate and discount rate.',
          'Clarify that money supply changes are policy-mediated and not simple currency-printing assumptions.',
          'Reinforce differences among monetary policy, fiscal policy, revenue, and expenditures.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 4 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'economics',
    unitNumber: 5,
    title: 'Unit 5: Personal Finance',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 Economics',
    dateRange: 'November 14, 2025 - December 18, 2025 (Planner Window)',
    totalDays: 'Guide Header: Fall 8 A/B | Spring 8 A/B',
    sourceLabel: '2025-2026_Economics_Unit_5_Guide.pdf',
    sourceUrl:
      '/original_pdfs/economics/2025-2026_Economics_Unit_5_Guide.pdf',
    transcriptPath: '/guides/economics-unit5-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students build personal-finance fluency through saving, credit, loans, insurance, housing decisions, and long-term planning.',
          'The unit emphasizes informed consumer decision making and connections between personal choices and long-term economic well-being.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Pacing Highlights',
        bullets: [
          'Fall window: November 14-December 18, 2025.',
          'Spring repeat window: April 28-June 5, 2026.',
          'Lesson sequence includes savings strategies, investments, credit/loans, insurance and bankruptcy, and budgeting with housing tradeoffs.'
        ]
      },
      {
        id: 'source-notes',
        heading: 'Guide Source Notes',
        bullets: [
          'The Unit 5 PDF header shows Fall November 15-December 11 and Spring April 21-May 30.',
          'To stay aligned with lessons and planner dates used in-app, this guide uses the district scope-sequence/planner windows: Fall November 14-December 18 and Spring April 28-June 5.',
          'The Unit 5 source file appears to carry over some Unit 4 prompt text, so the essential questions shown here are aligned to Unit 5 personal-finance content and lesson sequence.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes E.17B, E.17C, E.18A, and E.19A.',
          'Supporting emphasis includes E.17D, E.17E, E.17F, E.18B, E.18C, and E.18D.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How do savings plans and delayed gratification support long-term financial goals?',
          'How do credit behaviors and loan choices affect borrowing costs and financial risk?',
          'When is renting versus buying more advantageous for long-term financial planning?',
          'How should individuals evaluate insurance, debt management, and bankruptcy tradeoffs?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Complete the Unit 5 performance task using district rubric criteria.',
          'Show evidence of practical financial reasoning across saving, borrowing, housing, insurance, and budgeting scenarios.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address misconceptions about credit-card interest and long-term debt accumulation.',
          'Clarify secured versus unsecured borrowing and borrower obligations.',
          'Reinforce budget-first reasoning before investment and major housing decisions.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 5 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 1,
    title: 'Unit 1: Prosperity and World Influence',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'August 4, 2025 - October 7, 2025',
    totalDays: '14 A/B days',
    sourceLabel: '2025-2026_DRAFT_Unit__1_Guide_HS_U.S._History__11th_Grade_-_Copy_1.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-2026_DRAFT_Unit__1_Guide_HS_U.S._History__11th_Grade_-_Copy_1.pdf',
    transcriptPath: '/guides/us-history-unit1-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students analyze the Gilded Age through the Roaring Twenties, including industrial growth, reform movements, U.S. expansion, World War I, and social-cultural change.',
          'Instruction emphasizes source credibility, constitutional reasoning, and evidence-based historical argumentation.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes US.2A, US.3A-C, US.4A/C/F, US.5A-B, US.6A, US.9B, US.12A, US.13A-B, US.14A, US.15B-D, US.18B, US.22A, US.25A, US.26A, US.27A.',
          'Processing emphasis includes US.28A-D, US.29A-B, US.30A-B.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Pacing Highlights',
        bullets: [
          'Sub-unit 1: Historical inquiry and founding principles review',
          'Sub-unit 2: Frontier closure, industrialization, immigration, labor',
          'Sub-unit 3: Progressive reforms, suffrage, civil rights foundations',
          'Sub-unit 4: Imperialism and World War I',
          'Sub-unit 5: Roaring Twenties and unit synthesis/performance task'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 1 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 2,
    title: 'Unit 2: Boom to Bust',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'October 20, 2025 - October 31, 2025',
    totalDays: '5 A/B days',
    sourceLabel: '2025-2026_Unit_2_Guide_HS_US_History_11th_Grade_2_1.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-2026_Unit_2_Guide_HS_US_History_11th_Grade_2_1.pdf',
    transcriptPath: '/guides/us-history-unit2-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students transition from the excess of the 1920s into the collapse of the Great Depression and the rebuilding era of the New Deal.',
          'Instruction emphasizes economic cause-and-effect, federal versus state roles, and long-term policy impacts that still shape American life.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: The Great Depression (2 A/B days)',
          'Sub-unit 2: The New Deal (3 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes US.2A, US.16B, US.16C, US.18A, US.19B, US.24B, and US.25C.',
          'Supporting emphasis includes US.2B, US.16D, US.16E, US.24A, US.25B, and US.26C.',
          'Process emphasis includes US.28A-B and US.29A with recurring source-analysis routines.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What factors led to the collapse of the economy and the Great Depression?',
          'What were the effects of the Great Depression on families, labor, and migration?',
          'To what extent did the New Deal reshape the role of the federal government in society and the economy?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Options',
        bullets: [
          'Economic policy simulation with stakeholder roles (farmers, workers, officials, business owners).',
          'Structured New Deal legislative debate on effectiveness and government expansion.',
          'Dust Bowl case study analysis tied to policy design and environmental-economic interdependence.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address confusion about federal and state government roles before and during the New Deal.',
          'Clarify causes of the Great Depression beyond the stock market crash alone (tariffs, monetary policy, bank failures, speculation).',
          'Differentiate deportation/repatriation patterns in the 1930s from other immigration policy eras.',
          'Teach court-packing and checks-and-balances using FDR and Supreme Court conflict as a constitutional case study.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 2 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 3,
    title: 'Unit 3: World War II',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'November 3, 2025 - November 21, 2025',
    totalDays: '7 A/B days',
    sourceLabel: '2025-2026_Unit_3_Guide_HS_US_History_11th_grade_1.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-2026_Unit_3_Guide_HS_US_History_11th_grade_1.pdf',
    transcriptPath: '/guides/us-history-unit3-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students analyze causes of World War II, U.S. mobilization, and the two-front war across Europe and the Pacific.',
          'The guide links military strategy, homefront transformation, minority and women contributions, internment, and postwar geopolitical transition into the Cold War.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Causes of World War II (2 A/B days)',
          'Sub-unit 2: Mobilization and the Homefront (3 A/B days)',
          'Sub-unit 3: Two Front War (2 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes US.2A, US.7A, US.7C, US.15D, and US.17A.',
          'Supporting emphasis includes US.2B, US.7B, US.7D, US.7E, US.7F, US.7G, and US.23A.',
          'Process emphasis includes US.28A-E and US.29A with recurring source validation and argument routines.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'Why did the United States enter World War II, and how did U.S. production help produce victory?',
          'How did World War II impact Americans at home and abroad?',
          'How did World War II change the role of women in American society?',
          'How did World War II lead into the Cold War?',
          'Who were the American allies during World War II?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Multimedia presentation that connects Holocaust themes (prejudice, resilience, human rights) to contemporary discrimination and intolerance.',
          'Students synthesize historical and contemporary evidence to support claims about citizenship, social justice, and civic responsibility.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Correct the misconception that Pearl Harbor was the only cause of U.S. entry into war.',
          'Separate World War I and World War II causes, sequencing, and alliance structures.',
          'Clarify constitutional issues around Executive Order 9066 and internment policy.',
          'Distinguish Pacific and European theater strategy, including key turning points and minority-unit contributions.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 3 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 4,
    title: 'Unit 4: The Cold War',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'December 1, 2025 - December 19, 2025',
    totalDays: '7 A/B days',
    sourceLabel: '2025-2026_Unit_4_Guide_HS_US_History_11th_Grade.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-2026_Unit_4_Guide_HS_US_History_11th_Grade.pdf',
    transcriptPath: '/guides/us-history-unit4-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students examine Cold War policies at home and abroad, from postwar prosperity and domestic fears to containment strategy in Korea, Cuba, and Vietnam.',
          'The unit frames ideological conflict between U.S. democracy/capitalism and Soviet communism while emphasizing policy choices, constitutional tensions, and social impacts.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Strands',
        bullets: [
          'Prosperity and Culture of the 1950s',
          'Postwar Fears: McCarthyism, HUAC, arms race, and space race',
          'Containment: Truman Doctrine, alliances, and Cold War flashpoints'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes US.8A, US.8C, and US.17B with era anchoring in US.2A.',
          'Supporting emphasis includes US.1C, US.2B, US.7B, US.7D, US.8B, US.17C, US.23A, US.23B, and US.26B.',
          'Process emphasis includes US.28A-E and US.29A for analysis, corroboration, and communication.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What were the origins of the Cold War, and what impact did it have on U.S. politics and society?',
          'What were the short- and long-term effects of the Cold War?',
          'What major conflicts occurred during the Cold War, and how did they shape policy?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Students create a Cold War timeline synthesizing key domestic and foreign events from the era.',
          'Timeline products should connect turning points to policy shifts, constitutional issues, and social consequences.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address confusion between 1920s and 1950s Red Scare context.',
          'Clarify that Cold War refers to sustained ideological and geopolitical conflict, not one single battle.',
          'Differentiate NATO, the UN, and the Warsaw Pact with purpose and membership.',
          'Resolve common mix-ups: Berlin Airlift versus Berlin Wall, and Truman Doctrine versus earlier foreign-policy frameworks.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 4 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 5,
    title: 'Unit 5: Civil Rights and Vietnam War (1960s)',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'January 7, 2026 - February 11, 2026',
    totalDays: '12 A/B days',
    sourceLabel: '2025-2026_Unit_5_Guide_HS_US_History_11th_Grade_-_FINAL.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-2026_Unit_5_Guide_HS_US_History_11th_Grade_-_FINAL.pdf',
    transcriptPath: '/guides/us-history-unit5-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students trace the Civil Rights Movement from Reconstruction-era barriers through major court cases, legislation, and movement leadership in the 1960s.',
          'The unit also examines Vietnam War escalation, anti-war activism, constitutional debates, and civic participation changes such as the 26th Amendment.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Civil Rights (8 A/B days)',
          'Sub-unit 2: Vietnam War (4 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes US.2A, US.8D/F, US.9B/G/I, US.18B, US.22A, and US.25D.',
          'Supporting emphasis includes US.2B, US.8E, US.9D/J, and US.19A.',
          'Process emphasis includes US.28A-C and US.29A through primary-source inquiry and evidence-based communication.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What events expanded civil rights in the United States, and what political and social changes followed?',
          'What ideas shaped differing strategies among civil rights leaders and opponents?',
          'Why did the Civil Rights Movement gain major momentum in the 1960s?',
          'What major Supreme Court cases defined civil rights protections during this era?',
          'How did the Vietnam War engage students and the broader public in politics?',
          'How did U.S. intervention in Vietnam reflect containment policy in Asia?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Unit 5 Civil Rights performance task asks students to demonstrate understanding through evidence-rich products aligned to course standards.',
          'Vietnam subunit tasks emphasize cause-effect analysis of escalation, protest movements, and constitutional power debates.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address the misconception that the Civil Rights Movement only affected African Americans and only occurred in the 1960s.',
          'Clarify that court rulings do not create instant social change without executive and legislative enforcement.',
          'Differentiate key civil-rights cases and acts so students do not collapse them into one event.',
          'Correct oversimplifications of Vietnam (domino theory, Gulf of Tonkin, Tet Offensive, anti-war movement, and regional expansion beyond Vietnam).'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 5 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 6,
    title: 'Unit 6: Modern America',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'February 12, 2026 - March 31, 2026',
    totalDays: '12 A/B days',
    sourceLabel: '2025-2026_Unit_6_Guide_HS_US_History_11th_Grade.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-2026_Unit_6_Guide_HS_US_History_11th_Grade.pdf',
    transcriptPath: '/guides/us-history-unit6-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students examine U.S. presidential leadership and policy from the 1970s through the modern era, including Cold War closure, globalization, and domestic reform debates.',
          'Major themes include Middle East policy, Watergate, conservative resurgence, 9/11 and the War on Terror, economic crises, immigration, and technology-driven social change.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Subunit 1: End of the Cold War (1970-1990) - 5 A/B days',
          'Subunit 2: Modern Era (1990-present) - 7 A/B days'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis includes US.2A, US.10C, US.11A, US.12A, US.13B, US.17E, US.18B, US.19B, US.16A, and US.27A.',
          'Supporting emphasis includes US.2B, US.10A/B/D/E, US.11B/C/D, US.14B, US.17D, US.18C/D, US.23A, US.26C, and US.27B.',
          'Process emphasis includes US.28A-B, US.29A, and US.30A-B for analysis and visual representation tasks.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How did Nixon, Carter, and Reagan differ in foreign-policy strategy toward China, the Soviet Union, and the Middle East?',
          'How did environmentalism influence federal policy in the 1970s and 1980s?',
          'What economic shifts defined the U.S. from late 1970s stagflation through the 1990s and 2008 crisis?',
          'How did the end of the Cold War and post-9/11 era change U.S. military and domestic policy priorities?',
          'How did technology, globalization, and demographic change alter political and civic life in modern America?'
        ]
      },
      {
        id: 'historical-focus',
        heading: 'Historical Focus Areas',
        bullets: [
          '1970-1990: detente, China normalization, Camp David, Iran Hostage Crisis, conservative resurgence, and Reagan-era policy.',
          '1990-present: Persian Gulf War, Balkans conflict, 9/11 and War on Terror, Hurricane Katrina, financial crisis, and Obama-era reforms.',
          'Cross-cutting concepts include constitutional power, trust in government, and the economic effects of trade, innovation, and global interdependence.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Clarify electoral college versus popular vote in modern elections.',
          'Differentiate constitutional structures (federalism, separation of powers, checks and balances) in post-1970 case studies.',
          'Address student confusion across modern conflicts (Persian Gulf War, Balkans, 9/11, Iraq, and broader War on Terror).',
          'Connect levee failure, migration, energy markets, and communication technologies to concrete policy and quality-of-life outcomes.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 6 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 7,
    title: 'Unit 7: Founding Documents and STAAR Review',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'April 1, 2026 - April 16, 2026',
    totalDays: '1 A/B day',
    sourceLabel: '2025-_2026_US_History_Scope_and_Sequence_1.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-_2026_US_History_Scope_and_Sequence_1.pdf',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students complete a targeted review of foundational documents and civic principles in preparation for STAAR and end-of-course synthesis.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Supporting emphasis includes US.1A, US.1B, US.1C, US.21A, US.22D.',
          'Processing emphasis includes US.28A/B/D and US.29B.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Transcript unavailable for this unit. Use Lesson Sequence and Scope & Sequence references.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-history',
    unitNumber: 8,
    title: 'Unit 8: Projects and Performance Tasks',
    subtitle: '2025-2026 Curriculum Unit Guide - U.S. History Grade 11',
    dateRange: 'April 20, 2026 - June 3, 2026',
    totalDays: '12 A/B days',
    sourceLabel: '2025-_2026_US_History_Scope_and_Sequence_1.pdf',
    sourceUrl:
      '/original_pdfs/us_history/2025-_2026_US_History_Scope_and_Sequence_1.pdf',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students complete project-based demonstrations of historical thinking and TEKS mastery, using evidence and argumentation across major course themes.'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Teacher-selected integration of readiness, supporting, and processing TEKS.',
          'Use performance tasks and source-based products to demonstrate mastery.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Transcript unavailable for this unit. Use Lesson Sequence and Scope & Sequence references.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 1,
    title: 'Unit 1: Foundations and the U.S. Constitution',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'August 4, 2025 - September 10, 2025',
    totalDays: '13 A/B days',
    sourceLabel: '2025_U.S._Government_Unit_1_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_1_Guide.pdf',
    transcriptPath: '/guides/us-government-unit1-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students examine historical political ideas and philosophies that shaped American founding documents and constitutional design.',
          'The unit transitions from Enlightenment and foundational thinkers to constitutional structure, limited government, rights protections, and amendment procedures.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Foundations of Government (4 A/B days)',
          'Sub-unit 2: U.S. Constitution (8 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.1A-B, USG.6A, USG.6D.',
          'Supporting emphasis: USG.1C-D-E, USG.6C-E-F, USG.12A-B-C.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How do foundational ideas and beliefs shape governments and civic life?',
          'Why did the Framers include checks and balances, and how does federalism divide power?',
          'How does a written constitution protect rights while limiting government authority?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Create a civic infographic that informs citizens about fundamental principles of U.S. government.',
          'Product should include core theories, constitutional principles, and clear evidence-based explanations for a public audience.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address the misconception that the Constitution has never changed and cannot be amended.',
          'Clarify that founders adapted earlier ideas rather than inventing constitutional principles from scratch.',
          'Separate related but distinct concepts: republicanism, popular sovereignty, federalism, and separation of powers.',
          'Reinforce that individual rights are protected but not unlimited in every context.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 1 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 2,
    title: 'Unit 2: Federal Systems',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'September 11, 2025 - October 7, 2025',
    totalDays: '16 total days (8 A/B days)',
    sourceLabel: '2025_U.S._Government_Unit_2_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_2_Guide.pdf',
    transcriptPath: '/guides/us-government-unit2-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students analyze the federal system and compare how power is distributed across national, state, and local institutions.',
          'Instruction emphasizes constitutional structure, limited government, checks and balances, and the comparative strengths of federal versus unitary designs.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Federal System (8 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.7E, USG.7H, USG.8C, USG.11B.',
          'Supporting emphasis: USG.6B, USG.7G, USG.8A, USG.8D, USG.11A.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How does the U.S. federal system meet the needs of the people?',
          'What is one advantage and one disadvantage of federalism?',
          'How does federalism reflect limited government in constitutional design?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Students design a constitutional government model for a fictional nation and justify structure choices.',
          'Products must explain preamble goals, separation of powers, checks and balances, policy responsibilities, and citizen rights.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address the misconception that state governments have little real power in U.S. governance.',
          'Clarify that constitutional systems evolve through amendment and interpretation.',
          'Teach that separation of powers requires branch interaction, not branch isolation.',
          'Differentiate public input levels in policymaking and show how citizens influence outcomes beyond voting.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 2 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 3,
    title: 'Unit 3: Branches of Government',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'October 20, 2025 - November 7, 2025',
    totalDays: '14 total days (7 A/B days)',
    sourceLabel: '2025_U.S._Government_Unit_3_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_3_Guide.pdf',
    transcriptPath: '/guides/us-government-unit3-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students examine legislative, executive, and judicial branch structures, core functions, and constitutional limits.',
          'The unit connects branch interaction to elections, due process, rights interpretation, and everyday civic consequences.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Branches of Government (7 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.7A-B-C, USG.8A-B-D-E, USG.12A-B-C-F.',
          'Supporting emphasis: USG.1F, USG.7D, USG.9B-C, USG.12E-G.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How does the U.S. federal system of government meet the needs of the people?',
          'What roles do different branches of government have?',
          'What safeguards limit concentration of power in U.S. governance?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Students produce a multimedia civic briefing explaining branch functions, agencies, elections, amendments, and landmark rights cases.',
          'Products should connect constitutional structures to real civic life and include evidence-based historical examples.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Correct the misconception that branches never interact under separation of powers.',
          'Clarify how checks and balances constrain power but do not eliminate all institutional conflict.',
          'Differentiate Electoral College mechanics from general election outcomes.',
          'Reinforce due process and selective incorporation links to rights protection at state and federal levels.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 3 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 4,
    title: 'Unit 4: Policies and Politics',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'November 10, 2025 - December 12, 2025',
    totalDays: '18 total days (9 A/B days)',
    sourceLabel: '2025_U.S._Government_Unit_4_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_4_Guide.pdf',
    transcriptPath: '/guides/us-government-unit4-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Students evaluate public policy, economic regulation, and foreign-policy decision-making in constitutional context.',
          'The unit culminates in political-process analysis, including parties, interest groups, media influence, civic engagement, and citizen responsibilities.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Government Policies (9 A/B days)',
          'Sub-unit 2: The Political Process - We the People (9 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.3C, USG.4C, USG.5A, USG.9A, USG.13A, USG.14B-C, USG.15A-B, USG.18A-B.',
          'Supporting emphasis: USG.2A-B, USG.3A-B, USG.4A-B, USG.5B, USG.13B-C, USG.14A.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'What policies are enacted by the U.S. government to support economic stability?',
          'How does U.S. foreign policy protect national interests?',
          'How have legislation, policy, and court decisions shaped U.S. culture?',
          'How do parties, media, and interest groups influence political outcomes?',
          'Why is active civic engagement essential in constitutional democracy?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Students analyze policy pathways and civic influence using simulations, source evaluation, and argument-based products.',
          'Tasks prioritize constitutional reasoning, source reliability checks, and evidence-backed claims about policy effects.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address the misconception that policies are always effective and never revised.',
          'Differentiate federal and state legislative roles in policy design and implementation.',
          'Clarify that political participation includes advocacy, organizing, and information literacy, not only voting.',
          'Teach intended versus unintended policy consequences using economic and civic case studies.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Use the transcript viewer below for extracted text from the Unit 4 PDF.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 5,
    title: 'Unit 5: Foundations and the U.S. Constitution (Spring Cycle)',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'January 7, 2026 - February 13, 2026',
    totalDays: '13 A/B days',
    sourceLabel: '2025_U.S._Government_Unit_1_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_1_Guide.pdf',
    transcriptPath: '/guides/us-government-unit1-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Spring cycle revisits foundational political philosophy and constitutional principles with emphasis on application and civic argumentation.',
          'Students reinforce limited government, rights frameworks, and amendment-process reasoning in preparation for second-semester synthesis.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Foundations of Government (4 A/B days)',
          'Sub-unit 2: U.S. Constitution (8 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.1A-B, USG.6A, USG.6D.',
          'Supporting emphasis: USG.1C-D-E, USG.6C-E-F, USG.12A-B-C.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How do foundational ideas and beliefs shape governments and civic life?',
          'How does constitutional structure limit government while protecting rights?',
          'How does amendment preserve constitutional continuity while allowing change?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Create a civic infographic explaining core U.S. constitutional principles for a public audience.',
          'Use historical evidence and constitutional language to support explanations of rights and limits on power.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address the misconception that constitutional rights are absolute in every context.',
          'Clarify differences among republicanism, federalism, and popular sovereignty.',
          'Reinforce that constitutional interpretation evolves through amendment and judicial review.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Spring cycle uses the same foundational guide framework as Unit 1. Open transcript for full reference.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 6,
    title: 'Unit 6: Federal Systems (Spring Cycle)',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'February 19, 2026 - March 6, 2026',
    totalDays: '8 A/B days',
    sourceLabel: '2025_U.S._Government_Unit_2_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_2_Guide.pdf',
    transcriptPath: '/guides/us-government-unit2-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Spring cycle revisits federal systems, constitutional structure, and comparative government analysis.',
          'Students reapply federalism concepts through policy and institutional case studies.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Federal System (8 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.7E, USG.7H, USG.8C, USG.11B.',
          'Supporting emphasis: USG.6B, USG.7G, USG.8A, USG.8D, USG.11A.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How does the U.S. federal system meet the needs of the people?',
          'What tradeoffs come with shared power across national and state institutions?',
          'How does federalism operationalize limited government in practice?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Design and defend a constitutional government model for a fictional nation.',
          'Explain structure, checks and balances, policy authority, and civic rights in the final product.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Correct the misconception that federal government always overrides state authority.',
          'Clarify that separation of powers requires structured interaction, not branch isolation.',
          'Teach that policy legitimacy depends on constitutional constraints and civic participation.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Spring cycle uses the same federal-systems guide framework as Unit 2. Open transcript for full reference.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 7,
    title: 'Unit 7: Branches of Government (Spring Cycle)',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'March 23, 2026 - April 17, 2026',
    totalDays: '7 A/B days',
    sourceLabel: '2025_U.S._Government_Unit_3_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_3_Guide.pdf',
    transcriptPath: '/guides/us-government-unit3-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Spring cycle revisits branch structures, constitutional powers, and due-process protections.',
          'Students deepen analysis of rights jurisprudence, elections, and agency functions through applied civic scenarios.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Branches of Government (7 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.7A-B-C, USG.8A-B-D-E, USG.12A-B-C-F.',
          'Supporting emphasis: USG.1F, USG.7D, USG.9B-C, USG.12E-G.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How do the three branches share and limit power?',
          'How do elections, courts, and agencies affect daily civic life?',
          'How do constitutional safeguards prevent concentration of authority?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Produce a multimedia briefing on branch functions, rights protections, and constitutional checks.',
          'Support claims with case examples, constitutional language, and institutional evidence.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address confusion between branch powers and branch responsibilities.',
          'Differentiate judicial review from legislative lawmaking and executive enforcement.',
          'Reinforce due-process and incorporation concepts with concrete case evidence.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Spring cycle uses the same branches-of-government guide framework as Unit 3. Open transcript for full reference.'
        ]
      }
    ]
  },
  {
    courseKey: 'us-government',
    unitNumber: 8,
    title: 'Unit 8: Policies and Politics (Spring Cycle)',
    subtitle: '2025-2026 Curriculum Unit Guide - Grade 12 U.S. Government',
    dateRange: 'April 20, 2026 - May 22, 2026',
    totalDays: '12 A/B days',
    sourceLabel: '2025_U.S._Government_Unit_4_Guide.pdf',
    sourceUrl:
      '/original_pdfs/us_government/2025_U.S._Government_Unit_4_Guide.pdf',
    transcriptPath: '/guides/us-government-unit4-2025-2026.txt',
    sections: [
      {
        id: 'overview',
        heading: 'Unit Overview',
        paragraphs: [
          'Spring cycle revisits policy analysis and political-process participation with stronger emphasis on synthesis and civic application.',
          'Students integrate economic, legal, and political reasoning to evaluate how citizens and institutions shape governance outcomes.'
        ]
      },
      {
        id: 'pacing',
        heading: 'Sub-unit Pacing',
        bullets: [
          'Sub-unit 1: Government Policies (9 A/B days)',
          'Sub-unit 2: The Political Process - We the People (9 A/B days)'
        ]
      },
      {
        id: 'teks',
        heading: 'TEKS Focus',
        bullets: [
          'Readiness emphasis: USG.3C, USG.4C, USG.5A, USG.9A, USG.13A, USG.14B-C, USG.15A-B, USG.18A-B.',
          'Supporting emphasis: USG.2A-B, USG.3A-B, USG.4A-B, USG.5B, USG.13B-C, USG.14A.',
          'Process emphasis: USG.19A-B-C-F, USG.20B, USG.21B.'
        ]
      },
      {
        id: 'essential-questions',
        heading: 'Big Ideas and Essential Questions',
        bullets: [
          'How do policy tools shape economic stability and public outcomes?',
          'How do parties, interest groups, media, and citizens influence political change?',
          'Why is informed civic participation necessary for constitutional democracy?'
        ]
      },
      {
        id: 'performance-task',
        heading: 'Performance Task Focus',
        bullets: [
          'Analyze a contemporary policy pathway from proposal through implementation and public impact.',
          'Use source-evaluation and constitutional reasoning to defend claims about policy effectiveness.'
        ]
      },
      {
        id: 'instructional-guardrails',
        heading: 'Instructional Guardrails',
        bullets: [
          'Address the misconception that voting is the only meaningful form of political participation.',
          'Differentiate policy intent from policy outcomes, including unintended consequences.',
          'Clarify federal versus state authority in economic and civic policy contexts.'
        ]
      },
      {
        id: 'full-transcript',
        heading: 'Full Guide Transcript',
        paragraphs: [
          'Spring cycle uses the same policies-and-politics guide framework as Unit 4. Open transcript for full reference.'
        ]
      }
    ]
  }
];

const getCourseKeyFromName = (courseName = '') => {
  const normalized = String(courseName).toLowerCase();
  if (normalized.includes('physics')) return 'physics';
  if (normalized.includes('economics')) return 'economics';
  if (normalized.includes('united states government')) return 'us-government';
  if (normalized.includes('us government')) return 'us-government';
  if (normalized.includes('u.s. government')) return 'us-government';
  if (normalized === 'government') return 'us-government';
  if (normalized.includes('united states history')) return 'us-history';
  if (normalized.includes('us history')) return 'us-history';
  if (normalized.includes('u.s. history')) return 'us-history';
  return '';
};

export const getGuideByCourseAndUnit = (courseName, unitNumber) => {
  const courseKey = getCourseKeyFromName(courseName);
  if (!courseKey) return null;
  return (
    unitGuides.find(
      (guide) =>
        guide.courseKey === courseKey && guide.unitNumber === Number(unitNumber),
    ) || null
  );
};
